import { ClipboardIcon } from "@heroicons/react/outline";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  requestListRedeemedVoucher,
  requestListVoucher,
  useAuthState,
  useCheckoutDispatch,
  useCheckoutState,
} from "../../Context";
import { currencyFormat } from "../../Utils";
import { EmptyList } from "../../Components/EmptyList";
import { Alert } from "../../Components/Alert";
import { useTranslation } from "react-i18next";
import Badge from "../../Components/Badge";
import duration from "dayjs/plugin/duration";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isToday from "dayjs/plugin/isToday";
import isBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(isToday);
dayjs.extend(isBefore);

const CopyButton = ({ title }) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (err) {}
  };

  return (
    <button
      onClick={() => copyToClipboard(title)}
      className={`relative w-0 flex-1 inline-flex items-center justify-center py-4 text-sm font-medium border border-transparent rounded-br-lg ${
        isCopied ? "text-green-600" : "text-gray-700 hover:text-gray-500"
      } transition-colors duration-200`}
    >
      <ClipboardIcon
        className={`w-5 h-5 ${isCopied ? "text-green-600" : "text-gray-400"}`}
        aria-hidden="true"
      />
      <span className="ml-3">{isCopied ? t("codeCopied") : t("copyCode")}</span>
    </button>
  );
};

const Voucher = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useCheckoutDispatch();
  const {
    vouchers,
    loading,
    error,
    redeemedVouchers,
    redeemedVoucherLoading
  } = useCheckoutState();

  const { user } = useAuthState();

  const sortedValidVouchers = useMemo(() => {
    const now = dayjs();

    return vouchers
      .filter((voucher) => {
        return !voucher.expiredAt || dayjs(voucher.expiredAt).isAfter(now);
      })
      .sort((a, b) => {
        const expiredA = !a.expiredAt || dayjs(a.expiredAt).isBefore(now);
        const expiredB = !b.expiredAt || dayjs(b.expiredAt).isBefore(now);

        // Sort by expiration status
        if (expiredA && !expiredB) return 1;
        if (!expiredA && expiredB) return -1;

        // Both are valid or both are null; sort by expiration date
        if (a.expiredAt && b.expiredAt) {
          return dayjs(a.expiredAt).diff(dayjs(b.expiredAt));
        }

        // If one is null, treat it as later than any valid date
        return a.expiredAt ? -1 : 1;
      });
  }, [vouchers]);

  const usableVouchers = useMemo(() => {
    const redemptionCounts = redeemedVouchers?.reduce((acc, voucher) => {
      if (sortedValidVouchers.some((v) => v.id === voucher.voucherID)) {
        acc[voucher.voucherID] = (acc[voucher.voucherID] || 0) + 1;
      }
      return acc;
    }, {});

    return sortedValidVouchers.filter((voucher) => {
      const redeemedCount = redemptionCounts[voucher.id] || 0;
      return redeemedCount < voucher.limit;
    });
  }, [redeemedVouchers, sortedValidVouchers]);

  useEffect(() => {
    if (user.id) {
      requestListVoucher(dispatch, { userID: user.id });
      requestListRedeemedVoucher(dispatch, { userID: user.id });
    }
  }, [dispatch, user.id]);

  const formatTotal = (voucher) => {
    if (voucher.type === "PERCENTAGE") {
      return `${voucher.total}%`;
    } else if (voucher.type === "AMOUNT") {
      return currencyFormat(voucher.total);
    }
    return voucher.total;
  };

  const SkeletonLoading = () => {
    const skeletonItems = Array(6).fill(null);

    return (
      <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {skeletonItems.map((_, index) => (
          <li
            key={index}
            className="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200 animate-pulse"
          >
            <div className="w-full flex items-center justify-between p-6 space-x-6">
              <div className="flex-1 truncate">
                <div className="flex items-center space-x-3">
                  <div className="h-5 bg-gray-300 rounded w-24"></div>
                </div>
                <div className="mt-1 h-4 bg-gray-300 rounded w-40"></div>
              </div>
              <div className="flex-1 truncate">
                <div className="flex justify-end space-x-3">
                  <div className="h-5 bg-gray-300 rounded w-24"></div>
                </div>
                <div className="mt-1 h-4 bg-gray-300 rounded w-40"></div>
              </div>
            </div>
            <div>
              <div className="-mt-px flex divide-x divide-gray-200">
                <div className="-ml-px w-0 flex-1 flex py-2 justify-center">
                  <div className="h-5 bg-gray-300 rounded w-24"></div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  if (loading || redeemedVoucherLoading) {
    return <SkeletonLoading />;
  }

  if (error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error?.message}
          errors={error?.errors}
        />
      </div>
    );
  }

  return (
    <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {usableVouchers.length > 0 ? (
        usableVouchers.map((voucher) => {
          const expirationDate = dayjs(voucher.expiredAt);
          const now = dayjs();
          return (
            <li
              key={voucher.id}
              className="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200 flex flex-col"
            >
              <div className="w-full flex justify-between p-6 space-x-6 flex-1">
                <div className="flex-[6] flex flex-col justify-start items-start truncate">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-gray-900 text-sm font-medium truncate">
                      {voucher.id}
                    </h3>
                  </div>
                  <p className="mt-1 text-gray-500 text-xs whitespace-pre-line">
                    {voucher.description}
                  </p>
                </div>
                <div className="flex-[4] flex flex-col justify-start items-end truncate">
                  <div className="flex justify-end space-x-3">
                    <Badge title={formatTotal(voucher)} type="success" />
                  </div>
                  <p className="mt-1 text-right text-gray-500 text-xs truncate">
                    {voucher.expiredAt &&
                      (() => {
                        if (expirationDate.isToday()) {
                          const hoursLeft = expirationDate.diff(now, "hour");
                          return t("countHoursLeft", { hoursLeft });
                        } else {
                          return t("untilDate", {
                            date: expirationDate
                              .locale(i18n.language)
                              .format("DD MMM YY"),
                          });
                        }
                      })()}
                  </p>
                </div>
              </div>
              <div>
                <div className="-mt-px flex divide-x divide-gray-200">
                  <div className="-ml-px w-0 flex-1 flex">
                    <CopyButton title={voucher.id} />
                  </div>
                </div>
              </div>
            </li>
          );
        })
      ) : (
        <div className="bg-white col-span-12 rounded-lg p-4">
          <EmptyList title="noVouchersAvailable" />
        </div>
      )}
    </ul>
  );
};

export default Voucher;
